import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../cn";

/**
 * One control surface for every text field, select and textarea: hairline border,
 * 8px control radius, the shared .focus-ring token (workspace-accent tinted), one
 * size scale. Replaces the duplicated local `inputCls` strings and raw
 * `focus:ring-2 focus:ring-brand` patterns scattered across screens.
 */
const controlCls =
  "focus-ring w-full rounded-control border border-line bg-card px-3 py-2 text-sm text-ink placeholder:text-faint disabled:cursor-not-allowed disabled:opacity-60 aria-[invalid=true]:border-bad";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(controlCls, className)} {...props} />
  ),
);
Input.displayName = "Input";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, rows = 3, ...props }, ref) => (
    <textarea ref={ref} rows={rows} className={cn(controlCls, "resize-y leading-relaxed", className)} {...props} />
  ),
);
Textarea.displayName = "Textarea";

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {}
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, ...props }, ref) => (
    <div className="relative">
      <select
        ref={ref}
        className={cn(controlCls, "appearance-none pr-9", className)}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
        aria-hidden
      />
    </div>
  ),
);
Select.displayName = "Select";

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Inline label rendered to the right of the box. */
  label?: React.ReactNode;
}
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, id, ...props }, ref) => {
    const reactId = React.useId();
    const inputId = id ?? reactId;
    const box = (
      <input
        ref={ref}
        id={inputId}
        type="checkbox"
        className={cn(
          "focus-ring h-4 w-4 shrink-0 rounded-xs border-strong text-brand accent-brand disabled:cursor-not-allowed disabled:opacity-60",
          className,
        )}
        {...props}
      />
    );
    if (!label) return box;
    return (
      <label htmlFor={inputId} className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink">
        {box}
        <span>{label}</span>
      </label>
    );
  },
);
Checkbox.displayName = "Checkbox";

/**
 * Label + control wrapper: a single field anatomy (label, optional required mark,
 * the control, then helper or error text). When `error` is set it overrides helper
 * and wires `aria-invalid` + `aria-describedby` onto the wrapped control.
 */
export function Field({
  label,
  htmlFor,
  helper,
  error,
  required,
  className,
  children,
}: {
  label?: React.ReactNode;
  /** Associates the label with a control's id; also used to derive the message id. */
  htmlFor?: string;
  /** Quiet helper line under the control. */
  helper?: React.ReactNode;
  /** When present, renders in danger tone and overrides the helper line. */
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const reactId = React.useId();
  const msgId = (htmlFor ?? reactId) + "-msg";
  const message = error ?? helper;
  // If exactly one element child is wrapped, thread describedby/invalid onto it.
  const enhanced =
    React.isValidElement(children) && message
      ? React.cloneElement(children as React.ReactElement, {
          "aria-describedby": msgId,
          ...(error ? { "aria-invalid": true } : {}),
        })
      : children;
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">
          {label}
          {required && <span className="ml-0.5 text-bad" aria-hidden>*</span>}
        </label>
      )}
      {enhanced}
      {message && (
        <p id={msgId} className={cn("text-xs", error ? "text-bad" : "text-faint")}>
          {message}
        </p>
      )}
    </div>
  );
}
